import { useTranslation } from 'react-i18next'

import { useSongContext } from 'SongContext'
import type { QueueData, StatusData } from 'types/api'
import getEnv from 'utils/getEnv'

import { repeatableFetch } from './useBundle'

export async function post(
  url: string,
  verb: 'post' | 'delete' = 'post',
): ReturnType<typeof fetch> {
  const response = await fetch(url, {
    method: verb,
    headers: {
      'content-type': 'application/json',
    },
  })
  return response
}

type SongManagerReturn = {
  getStatus: () => Promise<void>
  getQueue: () => Promise<void>
  sendSong: (id: number) => Promise<number>
  coolDowns: number[]
}

export function useSongManager(): SongManagerReturn {
  const { t } = useTranslation()
  const {
    setCurrentSongTitle,
    setIntermission,
    setQueueFull,
    setCoolDowns,
    coolDowns,
  } = useSongContext()

  const env = getEnv()

  const fetchAndParse = async <TResponse>(url: string): Promise<TResponse> => {
    const response = await repeatableFetch(url)

    if (!response.ok) {
      const json = await response.json()
      throw json || { code: response.status, detail: response.statusText }
    }

    const json = await response.json()

    return json
  }

  const getStatus = async (): Promise<void> => {
    const data = await fetchAndParse<StatusData>(
      `${env.VITE_APP_API_URL}status`,
    )

    console.info('getStatus', data)

    setIntermission(data.intermission)
    setQueueFull(data.queue_full)
    setCoolDowns(data.on_cooldown.map((id) => parseInt(id, 10)))
  }

  const getQueue = async (): Promise<void> => {
    const data = await fetchAndParse<QueueData>(`${env.VITE_APP_API_URL}queue`)

    console.info('getQueue', data)

    if (data.length > 0) {
      setCurrentSongTitle(`${data[0].title} by ${data[0].artist}`)
    } else {
      setCurrentSongTitle(t('no-song'))
    }
  }

  const sendSong = async (id: number): Promise<number> => {
    return await post(`${env.VITE_APP_API_URL}song/${id}/queue`, 'post')
      .then((res) => {
        console.info(`Song ID: ${id} - ${res.status}`)
        return res.status
      })
      .catch((_) => {
        throw new Error("Couldn't send song")
      })
  }

  return {
    getStatus,
    getQueue,
    sendSong,
    coolDowns,
  }
}
